import React, { useState, useEffect, useMemo } from 'react'
import api from '../../utils/api'
import { getPeriodDateRange, PERIOD_OPTIONS } from '../../utils/statsPeriod'
import ViewingCompanyChip from '../../components/company/ViewingCompanyChip'

const PERIOD_LABELS = PERIOD_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.label }), {})

const getIntegrationErrorTotal = (stat) => (stat?.daily || []).reduce((sum, d) => sum + (d.error || 0), 0)

const formatDate = (dateStr) => {
  const d = new Date(dateStr)
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

// error_message is str(error) + "\n\n" + traceback.format_exc() (see
// StepContext.fail in event_tracing_service.py). Some call sites report a
// failure via a manually built Exception outside any active `except` block
// (e.g. a connector returning status="error" - see cloudevent_processor.py) -
// traceback.format_exc() then has no exception context to read and appends
// the literal "NoneType: None" instead of a real traceback. That's fixed at
// the source going forward, but older rows can still carry it, so strip it
// here too - only as a TRAILING artifact, never from the middle, so what's
// left stays an exact prefix of the raw stored value and still matches the
// backend's substring search (see _search_condition in
// src/routes/event_traces.py) when copied.
const getTraceError = (trace) => {
  const failedStep = trace.steps?.find((s) => s.status === 'failed' && s.error_message)
  if (!failedStep?.error_message) return null
  const raw = String(failedStep.error_message)
  const cleaned = raw.replace(/(\s*NoneType:\s*None\s*)+$/i, '').trim()
  return cleaned || null
}

const copyToClipboard = (text, e) => {
  e.stopPropagation()
  navigator.clipboard.writeText(text).catch(() => {})
}

// Turns an error message into a shape-only "signature" for grouping - e.g.
// "Exame não encontrado para o AN: 1234" and "...AN: 5678" both become
// "Exame não encontrado para o AN: #", so the incidence panel can group
// occurrences of the same failure even when it carries a different id/number
// each time. Heuristic (digits only), not a real templating engine.
const buildErrorSignature = (text) => text.replace(/\d+/g, '#').replace(/\s+/g, ' ').trim()

// Reached from the dashboard's "Falha" card - reuses the stats already fetched
// there (see IntegrationsOverview/CompanyHomePage) to build the left-hand error
// counts per integration without an extra request. Only the traces for the
// integration picked on the left are fetched here, on demand.
const IntegrationsErrorsPage = ({ stats, period, integrations, companyId, viewedCompany, onBack }) => {
  const errorRows = useMemo(() => {
    return (stats || [])
      .map((stat) => ({
        stat,
        integration: integrations?.find((i) => i.id === stat.integration_id),
        errorCount: getIntegrationErrorTotal(stat)
      }))
      .filter((row) => row.errorCount > 0)
      .sort((a, b) => b.errorCount - a.errorCount)
  }, [stats, integrations])

  // Group by supplier (fornecedor) so the left column reads as an accordion:
  // supplier header + logo, badge with the combined error total, integrations inside.
  const supplierGroups = useMemo(() => {
    const groups = new Map()
    errorRows.forEach((row) => {
      const supplier = row.integration?.supplier
      const key = supplier?.id || row.stat.supplier_name || 'sem-fornecedor'
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: supplier?.name || row.stat.supplier_name || 'Sem fornecedor',
          logoUrl: supplier?.logo_url || row.stat.supplier_logo_url || null,
          rows: [],
          errorCount: 0
        })
      }
      const group = groups.get(key)
      group.rows.push(row)
      group.errorCount += row.errorCount
    })
    return Array.from(groups.values()).sort((a, b) => b.errorCount - a.errorCount)
  }, [errorRows])

  const [expandedGroups, setExpandedGroups] = useState(() =>
    supplierGroups.length === 1 ? new Set([supplierGroups[0].key]) : new Set()
  )
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(() =>
    supplierGroups.length === 1 ? (supplierGroups[0].rows[0]?.stat.integration_id || null) : null
  )
  const [activeTab, setActiveTab] = useState('incidencias')

  // Incidence panel - groups every failed trace of the selected integration
  // (within the same dashboard period) that shares the clicked error's
  // signature (see buildErrorSignature).
  const [incidenceOpen, setIncidenceOpen] = useState(false)
  const [incidenceSignature, setIncidenceSignature] = useState('')
  const [incidenceRecords, setIncidenceRecords] = useState([])
  const [incidenceScanned, setIncidenceScanned] = useState(0)
  const [incidenceTotalFailed, setIncidenceTotalFailed] = useState(0)

  // Incidências tab - eagerly groups ALL failed traces of the selected
  // integration/period by signature, so the user can see which error hits
  // hardest and how many distinct kinds there are without opening any of
  // them individually.
  const [incidenceSummaryLoading, setIncidenceSummaryLoading] = useState(false)
  const [incidenceSummaryGroups, setIncidenceSummaryGroups] = useState([])
  const [incidenceSummaryScanned, setIncidenceSummaryScanned] = useState(0)
  const [incidenceSummaryTotal, setIncidenceSummaryTotal] = useState(0)
  const [incidenceSummaryIntegrationId, setIncidenceSummaryIntegrationId] = useState(null)

  const { dateFrom, dateTo } = getPeriodDateRange(period)

  // Opens the incidence panel from data already grouped by the Incidências
  // tab (loadIncidenceSummary) - no extra request needed, since that tab
  // already scanned the full batch.
  const openIncidenceGroup = (group) => {
    setIncidenceSignature(group.signature)
    setIncidenceRecords(group.records)
    setIncidenceScanned(incidenceSummaryScanned)
    setIncidenceTotalFailed(incidenceSummaryTotal)
    setIncidenceOpen(true)
  }

  const closeIncidence = () => setIncidenceOpen(false)

  useEffect(() => {
    if (!incidenceOpen) return
    const onKeyDown = (e) => { if (e.key === 'Escape') closeIncidence() }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [incidenceOpen])

  // Fetches every failed trace of the selected integration/period (paginating
  // past the API's 100-per-page cap, up to a safety limit) and groups them by
  // error signature, so the Incidências tab can show every distinct error and
  // its total count up front - not just the one the user happens to click.
  const loadIncidenceSummary = async (integrationId) => {
    if (!integrationId || !companyId) return
    setIncidenceSummaryLoading(true)
    try {
      const MAX_PAGES = 20 // up to 2000 failed traces scanned
      const baseParams = { page_size: 100, status: 'failed' }
      if (dateFrom) baseParams.date_from = dateFrom
      if (dateTo) baseParams.date_to = dateTo

      let allItems = []
      let totalFailed = 0
      let pageNum = 1
      for (;;) {
        const response = await api.get(
          `/api/event-traces/company/${companyId}/integration/${integrationId}`,
          { params: { ...baseParams, page: pageNum } }
        )
        const items = response.data.items || []
        totalFailed = response.data.total || 0
        allItems = allItems.concat(items)
        if (items.length === 0 || allItems.length >= totalFailed || pageNum >= MAX_PAGES) break
        pageNum += 1
      }

      const groupsByKey = new Map()
      allItems.forEach((trace) => {
        const err = getTraceError(trace)
        if (!err) return
        const signature = buildErrorSignature(err)
        const key = signature.toLowerCase()
        if (!groupsByKey.has(key)) {
          groupsByKey.set(key, { signature, count: 0, records: [] })
        }
        const group = groupsByKey.get(key)
        group.count += 1
        group.records.push(trace)
      })

      setIncidenceSummaryGroups(Array.from(groupsByKey.values()).sort((a, b) => b.count - a.count))
      setIncidenceSummaryScanned(allItems.length)
      setIncidenceSummaryTotal(totalFailed)
      setIncidenceSummaryIntegrationId(integrationId)
    } catch (error) {
      console.error('Error loading incidence summary:', error)
      setIncidenceSummaryGroups([])
    } finally {
      setIncidenceSummaryLoading(false)
    }
  }

  useEffect(() => {
    // Skip refetching (a multi-page scan) when this integration's summary is
    // already cached from a previous visit to this tab.
    if (activeTab === 'incidencias' && selectedIntegrationId && selectedIntegrationId !== incidenceSummaryIntegrationId) {
      loadIncidenceSummary(selectedIntegrationId)
    }
  }, [activeTab, selectedIntegrationId])

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectedRow = errorRows.find((row) => row.stat.integration_id === selectedIntegrationId)

  return (
    <div className="p-2 sm:p-3 h-[calc(100vh-90px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onBack}
          title="Voltar para o dashboard"
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition"
        >
          <span className="material-icons" style={{ fontSize: '20px' }}>arrow_back</span>
        </button>

        <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
          <span className="material-icons text-red-600 dark:text-red-400" style={{ fontSize: '18px' }}>error</span>
        </div>

        <h1 className="text-base font-bold text-gray-900 dark:text-white">Erros por Integração</h1>
        <ViewingCompanyChip company={viewedCompany} />

        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300">
          <span className="inline-flex items-center gap-1.5">
            <span className="material-icons" style={{ fontSize: '13px' }}>event</span>
            Período do dashboard:
          </span>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
            {PERIOD_LABELS[period] || '7 dias'}
          </span>
        </span>

        {selectedRow && (
          <span className="ml-auto inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              {(selectedRow.integration?.supplier?.logo_url || selectedRow.stat.supplier_logo_url) ? (
                <img
                  src={selectedRow.integration?.supplier?.logo_url || selectedRow.stat.supplier_logo_url}
                  alt=""
                  className="w-4 h-4 object-contain rounded flex-shrink-0"
                />
              ) : (
                <span className="material-icons flex-shrink-0" style={{ fontSize: '13px' }}>business</span>
              )}
              <span className="truncate max-w-[140px]">
                {selectedRow.integration?.supplier?.name || selectedRow.stat.supplier_name || 'Sem fornecedor'}
              </span>
            </span>
            <span className="material-icons flex-shrink-0" style={{ fontSize: '13px' }}>arrow_forward</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 truncate max-w-[180px]">
              {selectedRow.stat.integration_name}
            </span>
          </span>
        )}
      </div>

      {/* Two-column layout: suppliers/integrations | error records */}
      <div className="flex gap-4 flex-1 overflow-hidden">

        {/* Left — suppliers accordion with integrations + error badges */}
        <div className="w-80 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-y-auto">
          {supplierGroups.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              Nenhuma integração com erros neste período
            </div>
          ) : (
            <div className="p-2.5 space-y-2.5">
              {supplierGroups.map((group) => {
                const isExpanded = expandedGroups.has(group.key)
                return (
                  <div
                    key={group.key}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center justify-between gap-2 px-3.5 py-3 text-left bg-gray-50/80 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {group.logoUrl ? (
                          <img src={group.logoUrl} alt="" className="w-6 h-6 object-contain rounded flex-shrink-0" />
                        ) : (
                          <span className="material-icons text-gray-400 dark:text-gray-500 flex-shrink-0" style={{ fontSize: '18px' }}>business</span>
                        )}
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{group.name}</span>
                        <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                          {group.errorCount}
                        </span>
                      </div>
                      <span className="material-icons text-gray-400 dark:text-gray-500 flex-shrink-0" style={{ fontSize: '18px' }}>
                        {isExpanded ? 'expand_less' : 'expand_more'}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="p-2 pt-2.5 space-y-1.5 bg-white dark:bg-gray-800">
                        {group.rows.map(({ stat, errorCount }) => {
                          const isActive = stat.integration_id === selectedIntegrationId
                          return (
                            <button
                              key={stat.integration_id}
                              onClick={() => setSelectedIntegrationId(stat.integration_id)}
                              className={`w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2.5 text-left transition ${
                                isActive ? 'bg-blue-50 dark:bg-blue-900/25 ring-1 ring-inset ring-blue-200 dark:ring-blue-800' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                  <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '14px' }}>
                                    sync_alt
                                  </span>
                                </div>
                                <span className={`text-sm truncate ${isActive ? 'font-medium text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300'}`}>
                                  {stat.integration_name}
                                </span>
                              </div>
                              <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[11px] font-semibold bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/40">
                                {errorCount}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Right — error records for the selected integration */}
        <div className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
          {selectedIntegrationId && (
            <div className="flex items-center gap-1 px-3 pt-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                onClick={() => setActiveTab('incidencias')}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition ${
                  activeTab === 'incidencias'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <span className="material-icons" style={{ fontSize: '15px' }}>layers</span>
                Incidências
                {incidenceSummaryGroups.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {incidenceSummaryGroups.length}
                  </span>
                )}
              </button>
            </div>
          )}

          {!selectedIntegrationId ? (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <span className="material-icons mb-1 block" style={{ fontSize: '32px' }}>error_outline</span>
                <p className="text-xs font-medium">Selecione uma integração para ver os erros</p>
              </div>
            </div>
          ) : activeTab === 'incidencias' ? (
            incidenceSummaryLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin inline-block w-6 h-6 border-3 border-gray-200 border-t-blue-500 rounded-full mb-2"></div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Agrupando incidências...</p>
                </div>
              </div>
            ) : incidenceSummaryGroups.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <div className="text-center">
                  <span className="material-icons mb-1 block" style={{ fontSize: '32px' }}>check_circle</span>
                  <p className="text-xs font-medium">Nenhum erro encontrado para esta integração no período</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{incidenceSummaryGroups.length}</span>{' '}
                    {incidenceSummaryGroups.length === 1 ? 'tipo de erro' : 'tipos de erro'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{incidenceSummaryScanned}</span>{' '}
                    {incidenceSummaryScanned === 1 ? 'ocorrência analisada' : 'ocorrências analisadas'}
                  </span>
                  {incidenceSummaryTotal > incidenceSummaryScanned && (
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">(de {incidenceSummaryTotal} no total)</span>
                  )}
                </div>
                <div className="p-4 space-y-2.5">
                  {incidenceSummaryGroups.map((group, idx) => {
                    // % of ALL failed traces of this integration in the period
                    // (not just relative to the top offender), so the bar
                    // reads as "this error is N% of everything that failed".
                    // Kept to 1 decimal place so it matches what the user gets
                    // doing count/total by hand instead of a rounded-off number.
                    const pct = incidenceSummaryTotal > 0 ? (group.count / incidenceSummaryTotal) * 100 : 0
                    const pctLabel = pct.toFixed(1)
                    return (
                      <button
                        key={`${group.signature.toLowerCase()}-${idx}`}
                        onClick={() => openIncidenceGroup(group)}
                        className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition p-3.5"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2.5 min-w-0">
                            <span className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-[11px] font-bold text-gray-500 dark:text-gray-400">
                              {idx + 1}
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-200 break-all">{group.signature}</span>
                          </div>
                          <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[26px] h-[26px] px-2 rounded-full text-xs font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                            {group.count}
                          </span>
                        </div>
                        <div className="mt-3 flex items-center gap-2.5">
                          <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700/70 shadow-inner overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-amber-400 to-red-500 dark:from-amber-500 dark:to-red-500 transition-[width] duration-500 ease-out"
                              style={{ width: `${Math.max(pct, 4)}%` }}
                            />
                          </div>
                          <span className="flex-shrink-0 text-xs font-bold text-gray-700 dark:text-gray-200 tabular-nums w-12 text-right">
                            {pctLabel}%
                          </span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          ) : null}
        </div>
      </div>

      {/* Incidence panel - groups occurrences of the same error signature */}
      {incidenceOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 dark:bg-black/60 z-40"
            onClick={closeIncidence}
          />
          <div className="fixed inset-y-0 right-0 w-full max-w-4xl bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col">
            <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '18px' }}>layers</span>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-bold text-gray-900 dark:text-white">Incidência do erro</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">{incidenceSignature}</p>
              </div>
              <button
                onClick={closeIncidence}
                title="Fechar"
                className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition"
              >
                <span className="material-icons" style={{ fontSize: '18px' }}>close</span>
              </button>
            </div>

            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                <span className="material-icons" style={{ fontSize: '13px' }}>error</span>
                {incidenceRecords.length} {incidenceRecords.length === 1 ? 'ocorrência' : 'ocorrências'}
              </span>
              {incidenceTotalFailed > incidenceScanned && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 leading-relaxed">
                  Baseado nos {incidenceScanned} erros mais recentes desta integração no período (de {incidenceTotalFailed} no total) - variações com números/ids diferentes contam como a mesma ocorrência.
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {incidenceRecords.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  <p className="text-xs font-medium">Nenhuma outra ocorrência encontrada</p>
                </div>
              ) : (
                <div className="p-4 space-y-3">
                  {incidenceRecords.map((record) => (
                    <div
                      key={record.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 p-3.5"
                    >
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{record.source}</span>
                        <span className="text-[11px] font-mono text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">{formatDate(record.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mb-2.5">
                        <code className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 truncate max-w-[260px]">{record.trace_id}</code>
                        <button
                          onClick={(e) => copyToClipboard(record.trace_id, e)}
                          title="Copiar Trace ID"
                          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
                        >
                          <span className="material-icons" style={{ fontSize: '12px' }}>content_copy</span>
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 break-all leading-relaxed">{getTraceError(record)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default IntegrationsErrorsPage
